import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import {
  debounce,
  get,
  indexOf,
  isEmpty,
  map,
  reduce,
  lowerCase,
  includes,
  take,
  concat,
  find,
} from 'lodash';
import { fetchStatesGeography } from '../../actions';
import SelectField from '../SelectField';

const defaultLocationState = {
  citiesOptions: [],
  countiesOptions: [],
  citiesFullOptions: [],
  countiesFullOptions: [],
  citiesPlaceholder: 'Select state(s) first',
  countiesPlaceholder: 'Select state(s) first',
};

const SelectHint = () => (
  <div className="uu-select-hint">
    Start typing to search for results. (50 first)
  </div>
);

export default class ResidencyRequirements extends Component {
  constructor(props) {
    super(props);

    this.debouncedChange = debounce(props.onFieldChange, 400);
    this.fetchPlaces = this.fetchPlaces.bind(this);
    this.getChildren = this.getChildren.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onCountySearch = this.onCountySearch.bind(this);
    this.onCitySearch = this.onCitySearch.bind(this);
    this.onCityOrCountyChange = this.onCityOrCountyChange.bind(this);

    this.countyFilter = null;
    this.cityFilter = null;

    this.state = {
      ...defaultLocationState,
      enableChoices: get(window, 'uuSearchSettings.enable-choices', false),
    };
  }

  onCountySearch(event) {
    const {
      countiesFullOptions,
    } = this.state;
    const term = get(event, 'detail.value', null);
    this.setState({
      countiesOptions: this.cityOrCountiesSelectOptions(countiesFullOptions, 'counties', term),
    });
  }

  onCitySearch(event) {
    const {
      citiesFullOptions,
    } = this.state;
    const term = get(event, 'detail.value', null);
    this.setState({
      citiesOptions: this.cityOrCountiesSelectOptions(citiesFullOptions, 'places', term),
    });
  }

  onCityOrCountyChange(event) {
    const {
      onFieldChange,
    } = this.props;
    const {
      countiesFullOptions,
      citiesFullOptions,
    } = this.state;

    const key = get(event, 'target.name');
    const values = onFieldChange(event);

    if (key === 'counties') {
      this.setState({
        countiesOptions: this.cityOrCountiesSelectOptions(countiesFullOptions, 'counties', undefined, values),
      });
    } else {
      this.setState({
        citiesOptions: this.cityOrCountiesSelectOptions(citiesFullOptions, 'places', undefined, values),
      });
    }
  }

  getChildren(places, selected) {
    return map(places, (place = {}) => ({
      label: get(place, 'display_name', '').concat(', ', place.state),
      value: place.full_code,
      initial: indexOf(selected, place.full_code) >= 0,
    }), []);
  }

  fetchPlaces(states) {
    if (isEmpty(states)) {
      this.setState({
        ...defaultLocationState,
      });
      const { onFieldChange } = this.props;
      onFieldChange({ target: { name: 'counties' } });
      onFieldChange({ target: { name: 'cities' } });
      return;
    }

    const promises = Promise.all([
      fetchStatesGeography(states, 'counties'),
      fetchStatesGeography(states, 'places'),
    ]);

    promises.then(([counties, cities]) => {
      const countiesPlaceholder = isEmpty(states) ? defaultLocationState.countiesPlaceholder : null;
      const citiesPlaceholder = isEmpty(states) ? defaultLocationState.citiesPlaceholder : null;

      this.setState({
        countiesOptions: this.cityOrCountiesSelectOptions(counties, 'counties'),
        citiesOptions: this.cityOrCountiesSelectOptions(cities, 'places'),
        countiesFullOptions: counties,
        citiesFullOptions: cities,
        countiesPlaceholder,
        citiesPlaceholder,
      });
    });
  }

  cityOrCountiesSelectOptions(optionsList, key, term, passedValues) {
    const options = map(optionsList, (item) => {
      const children = reduce(item[key], (acc, child) => {
        if (term) {
          if (includes(lowerCase(child.display_name), lowerCase(term))) {
            return [
              ...acc,
              {
                value: child.full_code,
                display_name: child.display_name,
              },
            ];
          }
          return acc;
        }
        return [
          ...acc,
          {
            value: child.full_code,
            display_name: child.display_name,
          },
        ];
      }, []);

      const {
        values,
      } = this.props;

      const valuesChildren = reduce(passedValues || values[key], (acc, value) => {
        const child = find(item[key], { full_code: value });
        if (child) {
          return [
            ...acc,
            {
              value: child.full_code,
              display_name: child.display_name,
              disabled: true,
            },
          ];
        }
        return acc;
      }, []);

      return {
        value: item.state.code,
        display_name: item.state.display_name,
        children: isEmpty(children)
          ? [{ value: '', display_name: 'No Results', disabled: true }, ...valuesChildren]
          : concat(take(children, 50), valuesChildren),
      };
    });

    return options;
  }

  handleChange(event) {
    const {
      onFieldChange,
    } = this.props;
    const values = onFieldChange(event);

    this.fetchPlaces(values);
  }

  render() {
    const {
      states,
      values,
    } = this.props;
    const {
      citiesOptions,
      citiesPlaceholder,
      countiesOptions,
      countiesPlaceholder,
      enableChoices,
    } = this.state;

    return (
      <div className="uu-residency-requirements">
        <div className="uu-states-select">
          <label htmlFor="uu-states">
            States
          </label>
          <SelectField
            id="uu-states"
            name="states"
            onChange={this.handleChange}
            multiple
            value={values.states}
            options={states}
            valueKey="value"
            labelKey="display_name"
          />
        </div>

        <div className="uu-counties-select">
          <label htmlFor="uu-counties">
            Counties
          </label>
          {
            enableChoices && !isEmpty(countiesOptions) && <SelectHint />
          }
          <SelectField
            id="uu-counties"
            name="counties"
            onChange={this.onCityOrCountyChange}
            multiple
            value={values.counties}
            options={countiesOptions}
            noChoices={countiesPlaceholder}
            onSearch={this.onCountySearch}
          />
        </div>

        <div className="uu-cities-select">
          <label htmlFor="uu-cities">
            Cities
          </label>
          {
            enableChoices && !isEmpty(citiesOptions) && <SelectHint />
          }
          <SelectField
            id="uu-cities"
            name="places"
            onChange={this.onCityOrCountyChange}
            multiple
            value={values.places}
            options={citiesOptions}
            noChoices={citiesPlaceholder}
            onSearch={this.onCitySearch}
          />
        </div>
      </div>
    );
  }
}

ResidencyRequirements.propTypes = {
  onFieldChange: PropTypes.func.isRequired,
  states: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./public/js/components/Search/ResidencyRequirements.jsx